import {
  CheckCircleIcon,
  CircleDashedIcon,
  CircleHalfIcon,
  type Icon,
  ListChecksIcon,
  ProhibitIcon,
} from '@phosphor-icons/react';

import classNames from '@/utils/classnames';

import { summarizeTodos, type TodoItem, type TodoStatus } from '../../stores/todos';

/**
 * Dedicated renderer for the `set_todos` tool. Instead of the generic tool row's JSON blob, it
 * shows the agent's checklist with a per-item status glyph and a summary count, and under each
 * finished item what justified it: the check behind a completed one, the reason behind a
 * cancelled one.
 */

const STATUS_META: Record<TodoStatus, { icon: Icon; className: string }> = {
  completed: { icon: CheckCircleIcon, className: 'text-success' },
  in_progress: { icon: CircleHalfIcon, className: 'text-brand-text' },
  pending: { icon: CircleDashedIcon, className: 'text-fg-tertiary' },
  cancelled: { icon: ProhibitIcon, className: 'text-fg-tertiary' },
};

const FINISHED: readonly TodoStatus[] = ['completed', 'cancelled'];

/** The bare checklist — shared by the tool card and the pinned progress indicator. */
export function TodoChecklist({ todos }: { todos: TodoItem[] }) {
  if (todos.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {todos.map((todo, i) => {
        const meta = STATUS_META[todo.status];
        const StatusIcon = meta.icon;
        const finished = FINISHED.includes(todo.status);
        return (
          <li key={todo.id || i} className="flex items-start gap-2">
            <StatusIcon
              size={14}
              weight={todo.status === 'pending' ? 'regular' : 'fill'}
              className={classNames('mt-px shrink-0', meta.className)}
            />
            <div className="flex min-w-0 flex-col">
              <span
                className={classNames(
                  'text-xs leading-[1.5]',
                  finished ? 'text-fg-tertiary line-through' : 'text-fg-secondary',
                )}
              >
                {todo.text}
              </span>
              {todo.note && <span className="text-2xs leading-[1.5] text-fg-tertiary">{todo.note}</span>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function TodoToolCard({ todos }: { todos: TodoItem[] }) {
  const summary = summarizeTodos(todos);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line-subtle bg-surface-1 px-3 py-2.5">
      <div className="flex items-center gap-2">
        <ListChecksIcon size={14} weight="bold" className="shrink-0 text-fg-secondary" />
        <span className="text-xs font-semibold text-fg">Todos</span>
        <span className="ml-auto text-2xs text-fg-tertiary tabular-nums">
          {summary.total} {summary.total === 1 ? 'todo' : 'todos'} · {summary.completed} completed
        </span>
      </div>
      <TodoChecklist todos={todos} />
    </div>
  );
}
